import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import SectionHeader from '../SectionHeader';
import { StatTile } from '../ui/StatTile';
import MonthYearSelector from '../MonthYearSelector';
import { useAccountBookStore } from '@/store/accountBookStore';
import { useEmployeeData, useWorkScheduleData, useEmployeePayroll } from '@/hooks';
import { getExpensesByUserFromShared } from '../../services';
import { firestoreService, type RecordDocument, type RepeatExpenseDocument } from '../../services/firestoreService';
import { buildCashflowMonth, type PaydayOutflow } from '../../utils/cashflow';

const won = (n: number): string => `${Math.round(n).toLocaleString('ko-KR')}`;

const CashflowCalendarCard: React.FC = () => {
  const userId = useAccountBookStore((s) => s.userId);
  const currentAccountNumber = useAccountBookStore((s) => s.currentAccountNumber);
  const currentAccountBook = useAccountBookStore((s) => s.currentAccountBook);
  const accountNumber = currentAccountNumber || currentAccountBook?.accountNumber;
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [records, setRecords] = useState<RecordDocument[]>([]);
  const [repeats, setRepeats] = useState<RepeatExpenseDocument[]>([]);

  const { employees } = useEmployeeData();
  const { schedules } = useWorkScheduleData(selectedDate);
  const { monthlyPayrollData } = useEmployeePayroll(employees, schedules, selectedDate);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    Promise.all([
      getExpensesByUserFromShared(userId),
      firestoreService.getRepeatExpensesFromShared(userId),
    ]).then(([recs, reps]) => {
      if (!alive) return;
      setRecords(recs);
      setRepeats(reps);
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth() + 1;

  const paydayOutflows: PaydayOutflow[] = useMemo(() => {
    const byDay = new Map<number, number>();
    for (const p of monthlyPayrollData) {
      const day = p.employee.payday;
      if (!day || p.totalSalary <= 0) continue;
      byDay.set(day, (byDay.get(day) ?? 0) + p.totalSalary);
    }
    return [...byDay.entries()].map(([day, amount]) => ({ day, amount }));
  }, [monthlyPayrollData]);

  const scopedRecords = useMemo(
    () => (accountNumber ? records.filter((r) => r.accountNumber === accountNumber) : records),
    [records, accountNumber],
  );
  const scopedRepeats = useMemo(
    () => (accountNumber ? repeats.filter((r) => r.accountNumber === accountNumber) : repeats),
    [repeats, accountNumber],
  );

  const cashflow = useMemo(
    () =>
      buildCashflowMonth({
        records: scopedRecords,
        repeats: scopedRepeats,
        paydayOutflows,
        year,
        month,
        today: new Date(),
      }),
    [scopedRecords, scopedRepeats, paydayOutflows, year, month],
  );

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=일

  const dayMap = useMemo(() => new Map(cashflow.days.map((d) => [d.day, d])), [cashflow.days]);
  const markerMap = useMemo(() => {
    const m = new Map<number, string[]>();
    for (const mk of cashflow.markers) {
      const arr = m.get(mk.day) ?? [];
      arr.push(`${mk.label} ${won(mk.amount)}`);
      m.set(mk.day, arr);
    }
    return m;
  }, [cashflow.markers]);

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'var(--bg-base)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-20)',
        p: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <SectionHeader
        size="card"
        eyebrow="자금"
        title="자금 흐름 캘린더"
        subtitle="이번 달 언제 돈이 들고 나는지 · 예정 지출은 추정치"
        action={<MonthYearSelector selectedDate={selectedDate} onDateChange={setSelectedDate} />}
      />

      <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <StatTile label="이번 달 수입" value={won(cashflow.totals.income)} tone="income" size="sm" autoFit />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile label="이번 달 지출" value={won(cashflow.totals.expense)} tone="expense" size="sm" autoFit />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            label="순액"
            value={won(cashflow.totals.net)}
            tone={cashflow.totals.net >= 0 ? 'profit' : 'loss'}
            size="sm"
            autoFit
          />
        </Grid>
      </Grid>

      {/* 요일 헤더 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
        {['일', '월', '화', '수', '목', '금', '토'].map((w) => (
          <Typography key={w} sx={{ fontSize: 'var(--size-xs)', color: 'var(--fg-tertiary)', textAlign: 'center' }}>
            {w}
          </Typography>
        ))}
      </Box>

      {/* 날짜 셀 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {Array.from({ length: firstWeekday }).map((_, i) => (
          <Box key={`blank-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const d = dayMap.get(day);
          const markers = markerMap.get(day);
          return (
            <Box
              key={day}
              sx={{
                minHeight: 64,
                borderRadius: 'var(--r-12)',
                border: '1px solid var(--border-subtle)',
                p: 0.75,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              <Typography sx={{ fontSize: 'var(--size-xs)', color: 'var(--fg-secondary)' }}>{day}</Typography>
              {d && d.income > 0 && (
                <Typography sx={{ fontSize: 'var(--size-xs)', color: 'var(--c-green-700)', fontFamily: 'var(--font-numeric)' }}>
                  +{won(d.income)}
                </Typography>
              )}
              {d && d.expense > 0 && (
                <Typography sx={{ fontSize: 'var(--size-xs)', color: 'var(--c-red-600)', fontFamily: 'var(--font-numeric)' }}>
                  -{won(d.expense)}
                </Typography>
              )}
              {markers?.map((label, k) => (
                <Typography
                  key={k}
                  sx={{
                    fontSize: 'var(--size-xs)',
                    color: 'var(--fg-tertiary)',
                    border: '1px dashed var(--border-subtle)',
                    borderRadius: 'var(--r-8)',
                    px: 0.5,
                    lineHeight: 1.3,
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default CashflowCalendarCard;
